import type { Command } from '@/core/Command';
import type { SiteObject } from '@/site/SiteObject';
import type { SiteObjectWorld } from '@/site/SiteObjectWorld';

/**
 * Undo records for buildings and paving.
 *
 * Site objects were introduced by the reference pipeline, which only ever built them as part of a
 * whole project — a load, not an edit, so it needed no history. The wand is the first thing that adds
 * a shed to a site that already exists, and an addition that cannot be taken back is not an edit.
 *
 * Records are snapshotted on construction. `SiteObjectWorld.add` adopts the object it is given, so
 * handing it the same reference twice would let a later edit reach backwards into the undo record.
 */

function snapshot(object: SiteObject): SiteObject {
  return {
    ...object,
    position: { ...object.position },
    ...(object.footprint ? { footprint: object.footprint.map((point) => ({ ...point })) } : {}),
    ...(object.masses
      ? {
          masses: object.masses.map((mass) => ({
            ...mass,
            footprint: mass.footprint.map((point) => ({ ...point })),
          })),
        }
      : {}),
  };
}

export class AddSiteObjectsCommand implements Command {
  readonly label: string;
  readonly kind = 'structure' as const;
  private readonly objects: SiteObject[];

  constructor(
    private readonly world: SiteObjectWorld,
    objects: readonly SiteObject[],
    label?: string,
  ) {
    this.objects = objects.map(snapshot);
    this.label = label ?? (objects.length === 1 ? 'Add structure' : `Add ${objects.length} structures`);
  }

  execute(): void {
    for (const object of this.objects) {
      if (!this.world.has(object.id)) this.world.add(snapshot(object));
    }
  }

  undo(): void {
    for (const object of this.objects) this.world.remove(object.id);
  }
}

/**
 * Move, turn, or otherwise edit one placed object.
 *
 * Singular, matching `UpdateFaunaCommand`, and for the same reason: the record it holds is a whole
 * object rather than a patch, so folding several into one entry is `CompositeCommand`'s job and not
 * a second constructor's. See `StaffageLayer.updateCommand`.
 */
export class UpdateSiteObjectCommand implements Command {
  readonly label: string;
  readonly kind = 'transform' as const;
  private readonly before: SiteObject;
  private readonly after: SiteObject;

  constructor(
    private readonly world: SiteObjectWorld,
    before: SiteObject,
    after: SiteObject,
    label = 'Adjust object',
  ) {
    this.before = snapshot(before);
    this.after = snapshot(after);
    this.label = label;
  }

  execute(): void {
    if (this.world.has(this.after.id)) this.world.update(snapshot(this.after));
  }

  undo(): void {
    if (this.world.has(this.before.id)) this.world.update(snapshot(this.before));
  }
}

export class RemoveSiteObjectsCommand implements Command {
  readonly label: string;
  readonly kind = 'remove' as const;
  private readonly objects: SiteObject[];

  constructor(
    private readonly world: SiteObjectWorld,
    objects: readonly SiteObject[],
    label?: string,
  ) {
    this.objects = objects.map(snapshot);
    this.label =
      label ?? (objects.length === 1 ? 'Remove structure' : `Remove ${objects.length} structures`);
  }

  execute(): void {
    for (const object of this.objects) this.world.remove(object.id);
  }

  undo(): void {
    for (const object of this.objects) {
      if (!this.world.has(object.id)) this.world.add(snapshot(object));
    }
  }
}
